/**
 * useWidgetData - Hook centralisé pour la récupération et transformation des données des widgets
 * 
 * Ce hook gère :
 * - La construction du payload API selon le type de widget
 * - L'appel à /widgets/run avec React Query
 * - La transformation des réponses en format universel
 * - La gestion des états loading/error
 */

import { useMemo } from "react";
import { useApiQuery } from "@/hooks/use-api";
import type { FiltersData } from "@/models/filter";
import { convertFiltersToQueryString } from "@/utils/filters-to-query-string";
import type {
  WidgetConfig,
  DataConfig,
  BucketDataConfig,
  CategoryDataConfig,
  GlobalMetricDataConfig,
  DocumentsDataConfig,
  ChartData,
  KpiData,
  TableData,
  DocumentsData,
  WidgetData,
} from "@/models/widget-v2";
import { toBackendWidgetType } from "@/models/widget-v2";

// ============================================================================
// Types
// ============================================================================

interface UseWidgetDataOptions {
  config: WidgetConfig;
  filters?: FiltersData;
  enabled?: boolean;
}

interface UseWidgetDataResult {
  data: WidgetData;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
}

// ============================================================================
// Payload Builders
// ============================================================================

/**
 * Construit le payload API pour un widget BucketMetric (line, bar, area, table)
 * Format backend: { chart: "line_widget", bucket_field, bucket_type, metrics, ... }
 */
function buildBucketPayload(config: BucketDataConfig, chartType: string): Record<string, unknown> {
  return {
    doc_type: chartType,
    chart: chartType,
    queries: [],
    bucket_field: config.bucketField,
    bucket_type: config.bucketType,
    metrics: config.metrics.map(m => ({
      name: m.name,
      type: m.type,
      field: m.field,
    })),
    size: config.size ?? 10,
    ...(config.bucketType === "date_histogram" && {
      date_interval: config.dateInterval ?? "month",
    }),
    ...(config.bucketType === "histogram" && {
      numeric_interval: config.numericInterval ?? 100,
    }),
    // Split by field for multiple series
    ...(config.splitBy && {
      split_by: config.splitBy,
      split_by_size: config.splitBySize ?? 20,
    }),
  };
}

/**
 * Construit le payload API pour un widget CategoryMetric (pie, wordcloud)
 * Format backend: { chart: "pie_widget", field, metric, size, ... }
 */
function buildCategoryPayload(config: CategoryDataConfig, chartType: string): Record<string, unknown> {
  return {
    doc_type: chartType,
    chart: chartType,
    queries: [],
    field: config.field,
    metric: {
      name: config.metric.name,
      type: config.metric.type,
      field: config.metric.field,
    },
    size: config.size ?? 10,
    min_doc_count: config.minDocCount ?? 1,
    show_missing: config.showMissing ?? false,
    missing_label: config.missingLabel ?? "Missing",
  };
}

/**
 * Construit le payload API pour un widget GlobalMetric (kpi, gauge)
 * Format backend: { chart: "kpi_widget", metric, comparison?, ... }
 */
function buildGlobalPayload(config: GlobalMetricDataConfig, chartType: string): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    doc_type: chartType,
    chart: chartType,
    queries: [],
    metric: {
      name: config.metric.name,
      type: config.metric.type,
      field: config.metric.field,
    },
  };
  
  if (config.comparison) {
    payload.comparison = {
      period: config.comparison.period,
      metric: {
        name: "previous",
        type: config.metric.type,
        field: config.metric.field,
      },
      date_field: config.comparison.dateField,
    };
  }
  
  return payload;
}

/**
 * Construit le payload API pour documents/graph
 * Format backend: { chart: "documents_widget", size, sort?, fields?, ... }
 */
function buildDocumentsPayload(config: DocumentsDataConfig): Record<string, unknown> {
  return {
    doc_type: "documents_widget",
    chart: "documents_widget",
    queries: [],
    size: config.size ?? 20,
    ...(config.sort && {
      sort: [{
        [config.sort.field]: { order: config.sort.order },
      }],
    }),
    ...(config.fields && { fields: config.fields }),
  };
}

/**
 * Construit le payload API complet selon la configuration du widget
 * Le payload est au format attendu par le backend (Widget interface)
 */
function buildApiPayload(config: WidgetConfig): Record<string, unknown> | null {
  const chartType = toBackendWidgetType(config.type);
  const dataConfig = config.data;
  
  switch (dataConfig.kind) {
    case "bucket":
      return buildBucketPayload(dataConfig, chartType);
    case "category":
      return buildCategoryPayload(dataConfig, chartType);
    case "global":
      return buildGlobalPayload(dataConfig, chartType);
    case "documents":
      return buildDocumentsPayload(dataConfig);
    case "static":
      return null; // Pas d'appel API pour les widgets statiques
    default:
      return null;
  }
}

// ============================================================================
// Response Transformers
// ============================================================================

interface AggregationBucket {
  key: string | number;
  key_as_string?: string;
  doc_count: number;
  [metricName: string]: unknown;
}

interface SplitSeriesBucket {
  key: string | number;
  key_as_string?: string;
  doc_count: number;
  x_axis_bucket: {
    buckets: AggregationBucket[];
  };
}

interface SplitSeriesAggregation {
  buckets: SplitSeriesBucket[];
}

/**
 * Extrait la valeur d'une métrique depuis un bucket
 */
function extractMetricValue(bucket: AggregationBucket | undefined, metric: { name: string; type: string }): number {
  if (!bucket) return 0;
  
  if (metric.type === "count") {
    return bucket.doc_count || 0;
  }
  const metricValue = bucket[metric.name];
  if (metricValue && typeof metricValue === "object" && "value" in metricValue) {
    return (metricValue as { value: number }).value || 0;
  }
  if (typeof metricValue === "number") {
    return metricValue;
  }
  return 0;
}

/**
 * Transforme la réponse API pour les widgets BucketMetric en ChartData
 * Gère les cas avec et sans split_by (séries multiples)
 */
function transformBucketResponse(
  response: Record<string, unknown>,
  config: BucketDataConfig
): ChartData {
  // Extraire les agrégations
  const aggregations = (response.aggregations as Record<string, unknown>) || response;
  
  // Cas avec split_by: plusieurs séries
  if (config.splitBy && aggregations.split_series) {
    const splitAgg = aggregations.split_series as SplitSeriesAggregation;
    
    if (!splitAgg?.buckets || !Array.isArray(splitAgg.buckets) || splitAgg.buckets.length === 0) {
      return { labels: [], datasets: [] };
    }
    
    // Collecter tous les labels uniques de toutes les séries
    const allLabelsSet = new Set<string>();
    splitAgg.buckets.forEach(seriesBucket => {
      if (seriesBucket.x_axis_bucket?.buckets) {
        seriesBucket.x_axis_bucket.buckets.forEach(b => {
          allLabelsSet.add(b.key_as_string || String(b.key));
        });
      }
    });
    
    // Trier les labels (important pour les dates)
    const labels = Array.from(allLabelsSet).sort();
    
    // Créer un dataset par combinaison série/métrique
    const datasets = splitAgg.buckets.flatMap(seriesBucket => {
      const seriesKey = seriesBucket.key_as_string || String(seriesBucket.key);
      const innerBuckets = seriesBucket.x_axis_bucket?.buckets || [];
      
      // Créer une map pour lookup rapide
      const bucketMap = new Map<string, AggregationBucket>();
      innerBuckets.forEach(b => {
        const key = b.key_as_string || String(b.key);
        bucketMap.set(key, b);
      });
      
      // Créer un dataset pour chaque métrique de cette série
      return config.metrics.map(metric => {
        const data = labels.map(label => {
          const bucket = bucketMap.get(label);
          return extractMetricValue(bucket, metric);
        });
        
        // Si une seule métrique (count), utiliser juste le nom de la série
        // Sinon, combiner série + métrique
        const label = config.metrics.length === 1 && config.metrics[0].type === "count"
          ? seriesKey
          : `${seriesKey} - ${metric.name}`;
        
        return {
          label,
          data,
        };
      });
    });
    
    return { labels, datasets };
  }
  
  // Cas sans split_by: logique existante (une seule série)
  const bucketKey = Object.keys(aggregations).find(k => k.includes("bucket")) || Object.keys(aggregations)[0];
  const aggregation = aggregations[bucketKey] as { buckets?: AggregationBucket[] } | undefined;
  
  if (!aggregation?.buckets || !Array.isArray(aggregation.buckets)) {
    return { labels: [], datasets: [] };
  }
  
  const buckets = aggregation.buckets;
  
  // Extraire les labels
  const labels = buckets.map(bucket => 
    bucket.key_as_string || String(bucket.key || "Unknown")
  );
  
  // Créer les datasets pour chaque métrique
  const datasets = config.metrics.map(metric => {
    const data = buckets.map(bucket => extractMetricValue(bucket, metric));
    
    return {
      label: metric.name,
      data,
    };
  });
  
  return { labels, datasets };
}

/**
 * Transforme la réponse API pour les widgets CategoryMetric en ChartData
 */
function transformCategoryResponse(response: Record<string, unknown>): ChartData {
  // Format attendu: { type?, data: [{label, value, key}], ... }
  // Note: type peut être null, on vérifie seulement que data existe et est un tableau
  if (Array.isArray(response.data)) {
    const items = response.data as Array<{ label?: string; key?: string; value?: number }>;
    return {
      labels: items.map(item => item.label || item.key || "Unknown"),
      datasets: [{
        label: "Value",
        data: items.map(item => item.value ?? 0),
      }],
    };
  }
  
  // Fallback: essayer le format bucket
  const aggregations = (response.aggregations as Record<string, unknown>) || response;
  const bucketKey = Object.keys(aggregations).find(k => k.includes("bucket")) || Object.keys(aggregations)[0];
  const aggregation = aggregations[bucketKey] as { buckets?: AggregationBucket[] } | undefined;
  
  if (!aggregation?.buckets) {
    return { labels: [], datasets: [] };
  }
  
  return {
    labels: aggregation.buckets.map(b => b.key_as_string || String(b.key)),
    datasets: [{
      label: "Value",
      data: aggregation.buckets.map(b => b.doc_count || 0),
    }],
  };
}

/**
 * Transforme la réponse API pour les widgets GlobalMetric en KpiData
 */
function transformGlobalResponse(response: Record<string, unknown>): KpiData {
  if (typeof response === "number") {
    return { value: response };
  }
  
  const value = (response.value as number) ?? 0;
  const result: KpiData = { value };
  
  if (response.change) {
    const change = response.change as { value?: number; percentage?: number; trend?: string };
    result.change = {
      value: change.value ?? 0,
      percentage: change.percentage ?? 0,
      trend: (change.trend as "up" | "down" | "neutral") ?? "neutral",
    };
  }
  
  if (response.comparison) {
    const comp = response.comparison as { current?: number; previous?: number; period?: string };
    result.comparison = {
      current: comp.current ?? value,
      previous: comp.previous ?? 0,
      period: comp.period ?? "month",
    };
  }
  
  return result;
}

/**
 * Transforme la réponse API pour les documents
 */
function transformDocumentsResponse(response: Record<string, unknown>): DocumentsData {
  if (response.documents && Array.isArray(response.documents)) {
    return {
      documents: response.documents as Array<Record<string, unknown>>,
      total: (response.total as number) ?? response.documents.length,
    };
  }
  
  // Fallback: la réponse est directement un tableau
  if (Array.isArray(response)) {
    return {
      documents: response as Array<Record<string, unknown>>,
      total: response.length,
    };
  }
  
  return { documents: [], total: 0 };
}

/**
 * Transforme la réponse API pour les tables
 */
function transformTableResponse(
  response: Record<string, unknown>,
  config: BucketDataConfig
): TableData {
  const chartData = transformBucketResponse(response, config);
  
  // Construire les colonnes
  const columns = [
    { key: config.bucketField, label: config.bucketField },
    ...config.metrics.map(m => ({ key: m.name, label: m.name })),
  ];
  
  // Construire les lignes
  const rows = chartData.labels.map((label, index) => {
    const row: Record<string, unknown> = { [config.bucketField]: label };
    chartData.datasets.forEach(dataset => {
      row[dataset.label] = dataset.data[index];
    });
    return row;
  });
  
  return { columns, rows };
}

/**
 * Transforme la réponse API selon le type de widget
 */
function transformResponse(
  response: Record<string, unknown> | null,
  config: WidgetConfig
): WidgetData {
  if (!response) return null;
  
  const dataConfig = config.data;
  
  switch (dataConfig.kind) {
    case "bucket":
      if (config.type === "table_widget") {
        return transformTableResponse(response, dataConfig);
      }
      return transformBucketResponse(response, dataConfig);
      
    case "category":
      return transformCategoryResponse(response);
      
    case "global":
      return transformGlobalResponse(response);
      
    case "documents":
      return transformDocumentsResponse(response);
      
    case "static":
      return null;
      
    default:
      return null;
  }
}

// ============================================================================
// Hook Principal
// ============================================================================

// Track concurrent requests for debugging
let activeRequests = 0;
let totalRequests = 0;

/**
 * Hook pour récupérer et transformer les données d'un widget
 */
export function useWidgetData({
  config,
  filters,
  enabled = true,
}: UseWidgetDataOptions): UseWidgetDataResult {
  // Construire le payload API
  const apiPayload = useMemo(() => buildApiPayload(config), [config]);
  
  // Construire la query string Elasticsearch (inclut trackers_id si présent, mais optionnel)
  const queryString = useMemo(() => {
    if (!filters) return "";
    return convertFiltersToQueryString(filters);
  }, [filters]);
  
  // Construire le body de la requête
  const requestData = useMemo(() => ({
    self: apiPayload,
    ...(filters && {
      query: queryString,
    }),
  }), [apiPayload, filters, queryString]);
  
  // Clé de cache stable
  const cacheKey = useMemo(() => {
    return JSON.stringify({ payload: apiPayload, query: queryString });
  }, [apiPayload, queryString]);
  
  // Appel API - les trackers sont optionnels (juste un filtre ES)
  const shouldFetch = enabled && apiPayload !== null && config.data.kind !== "static";
  
  const { data: rawData, isLoading, isError, error, refetch } = useApiQuery<Record<string, unknown>>(
    {
      url: "widgets/run",
      method: "POST",
      data: requestData,
    },
    {
      enabled: shouldFetch,
      staleTime: 30000, // 30 seconds
      gcTime: 5 * 60 * 1000, // 5 minutes — show stale widget data as placeholder when revisiting dashboards
    }
  );
  
  
  // Transformer les données
  const transformStartTime = Date.now();
  const transformedData = useMemo(() => {
    if (!rawData || !shouldFetch) return null;
    const result = transformResponse(rawData, config);
    return result;
  }, [rawData, config, shouldFetch]);
  
  return {
    data: transformedData,
    isLoading: shouldFetch && isLoading,
    isError,
    error: error as Error | null,
    refetch,
  };
}

// ============================================================================
// Exports
// ============================================================================

export type { UseWidgetDataOptions, UseWidgetDataResult };
